import React, { useContext, useState, useEffect } from "react";
import {
  View,
  Text,
  Alert,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  useWindowDimensions,
  ScrollView,
} from "react-native";
import { AuthContext } from "../Context/AuthContext";
import {
  collection,
  doc,
  getDocs,
  addDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import dayjs from "dayjs";
import GlobalStyles from "../components/StylenColor";
import QuestionCard from "../components/QuestionCard";
import Loader from "../components/Loader";
import FilledMessage from "../components/FilledMessage";
import { LinearGradient } from "expo-linear-gradient";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";
import { NetworkContext } from "../Context/NetworkContext";

// Desktop web imports
import { useDesktopDetect, DesktopTheme, EmployeeLayout, getScoreColor, getPillarColor } from "../components/web";

const { colors, typography, spacing, borderRadius, shadows } = DesktopTheme;

// Desktop Question Card Component
const DesktopQuestionCard = ({
  question,
  currentIndex,
  totalQuestions,
  responses,
  handleOptionSelect,
  handleRemarkChange,
  isLastQuestion,
  isSubmitting,
  handleNext,
  handleSubmit,
  pillarColor,
}) => {
  const [hoveredOption, setHoveredOption] = useState(null);
  const currentResponse = responses[currentIndex];
  const hasRemark = (currentResponse?.remark || "").trim().length > 0;
  const canProceed = currentResponse?.weight && hasRemark;

  return (
    <View style={desktopStyles.questionCard}>
      {/* Progress indicator */}
      <View style={desktopStyles.progressHeader}>
        <View style={desktopStyles.progressInfo}>
          <Text style={desktopStyles.questionNumber}>
            Question {currentIndex + 1} of {totalQuestions}
          </Text>
          <View style={desktopStyles.progressBarContainer}>
            <View
              style={[
                desktopStyles.progressBar,
                {
                  width: `${((currentIndex + 1) / totalQuestions) * 100}%`,
                  backgroundColor: pillarColor,
                },
              ]}
            />
          </View>
        </View>
      </View>

      <View style={desktopStyles.questionLayout}>
        <View style={desktopStyles.questionColumn}>
          <Text style={desktopStyles.questionText}>{question.question}</Text>

          <ScrollView
            style={desktopStyles.optionsScroll}
            contentContainerStyle={desktopStyles.optionsContainer}
            showsVerticalScrollIndicator={false}
          >
            {question.options.map((option, index) => {
              const weight = question.weights[index];
              const isSelected = currentResponse?.weight === weight;

              return (
                <TouchableOpacity
                  key={index}
                  style={[
                    desktopStyles.optionButton,
                    isSelected && [desktopStyles.optionButtonSelected, { borderColor: pillarColor }],
                    hoveredOption === index && desktopStyles.optionButtonHovered,
                  ]}
                  onPress={() => handleOptionSelect(weight)}
                  onMouseEnter={Platform.OS === 'web' ? () => setHoveredOption(index) : undefined}
                  onMouseLeave={Platform.OS === 'web' ? () => setHoveredOption(null) : undefined}
                >
                  <View style={[
                    desktopStyles.optionRadio,
                    isSelected && { borderColor: pillarColor, backgroundColor: pillarColor },
                  ]}>
                    {isSelected && <View style={desktopStyles.optionRadioInner} />}
                  </View>
                  <Text style={[
                    desktopStyles.optionText,
                    isSelected && { color: pillarColor, fontWeight: '600' },
                  ]}>
                    {option}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>

        <View style={desktopStyles.sideColumn}>
        <View style={desktopStyles.remarksBlock}>
          <Text style={desktopStyles.remarksLabel}>Remarks (Required)</Text>
          <textarea
            placeholder="Kindly elaborate existing practices, evidences, document no, name, and trends."
            style={{
              width: '100%',
              flex: 1,
              minHeight: 100,
                padding: 12,
                borderRadius: 8,
                border: `1px solid ${colors.border}`,
                fontFamily: typography.fontFamily.body,
                fontSize: typography.fontSize.body,
                backgroundColor: colors.cardBg,
                resize: 'none',
                outline: 'none',
                overflow: 'auto',
              }}
              value={currentResponse?.remark || ""}
              onChange={(e) => handleRemarkChange(e.target.value)}
              maxLength={300}
            />
            <Text style={desktopStyles.charCount}>
              {(currentResponse?.remark || "").length}/300
            </Text>
          </View>

          <View style={desktopStyles.navigationButtons}>
            {!isLastQuestion ? (
              <TouchableOpacity
                style={[
                  desktopStyles.navButton,
                  { backgroundColor: pillarColor },
                  !canProceed && desktopStyles.navButtonDisabled,
                ]}
                onPress={handleNext}
                disabled={!canProceed}
              >
                <Text style={desktopStyles.navButtonText}>Next Question</Text>
                <Text style={desktopStyles.navButtonIcon}>→</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[
                  desktopStyles.navButton,
                  desktopStyles.submitButton,
                  { backgroundColor: colors.success },
                  (!canProceed || isSubmitting) && desktopStyles.navButtonDisabled,
                ]}
                onPress={handleSubmit}
                disabled={!canProceed || isSubmitting}
              >
                {isSubmitting ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <>
                    <Text style={desktopStyles.navButtonText}>Submit Assessment</Text>
                    <Text style={desktopStyles.navButtonIcon}>✓</Text>
                  </>
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

// Desktop Questions View
const DesktopQuestionsView = ({
  topic,
  pillar,
  questions,
  currentQuestionIndex,
  responses,
  handleOptionSelect,
  handleRemarkChange,
  handleNext,
  handleSubmit,
  isSubmitting,
  navigation,
  user,
}) => {
  const pillarColor = getPillarColor(pillar);
  const answeredCount = Object.keys(responses).filter(k => responses[k]?.weight).length;
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  return (
    <EmployeeLayout
      title={topic}
      subtitle={`${pillar} Assessment`}
      userName={user?.name || user?.email}
      companyName={user?.companyName}
      showBackButton={true}
      onBack={() => navigation.goBack()}
    >
      <View style={desktopStyles.page}>
        {/* Topic Header */}
        <View style={desktopStyles.topicHeader}>
          <View style={[desktopStyles.pillarBadge, { backgroundColor: pillarColor + '20' }]}>
            <Text style={[desktopStyles.pillarBadgeText, { color: pillarColor }]}>
              {pillar}
            </Text>
          </View>
          <Text style={desktopStyles.topicTitle}>{topic}</Text>
          <Text style={desktopStyles.topicSubtitle}>
            {answeredCount} of {questions.length} questions answered
          </Text>
        </View>

        <View style={desktopStyles.questionArea}>
          {/* Question Card */}
          {questions.length > 0 && questions[currentQuestionIndex] && (
            <DesktopQuestionCard
              question={questions[currentQuestionIndex]}
              currentIndex={currentQuestionIndex}
              totalQuestions={questions.length}
              responses={responses}
              handleOptionSelect={handleOptionSelect}
              handleRemarkChange={handleRemarkChange}
              isLastQuestion={isLastQuestion}
              isSubmitting={isSubmitting}
              handleNext={handleNext}
              handleSubmit={handleSubmit}
              pillarColor={pillarColor}
            />
          )}

          {questions.length === 0 && (
            <View style={desktopStyles.emptyContainer}>
              <Text style={desktopStyles.emptyIcon}>📋</Text>
              <Text style={desktopStyles.emptyTitle}>No Questions Available</Text>
              <Text style={desktopStyles.emptyText}>
                There are no questions for this topic yet.
              </Text>
            </View>
          )}
        </View>
      </View>
    </EmployeeLayout>
  );
};

const QuestionsScreen = ({ navigation, route }) => {
  const { user, loading, companyId, companyName } = useContext(AuthContext);
  const { topic } = route.params;
  const { isConnected } = useContext(NetworkContext);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isAlreadyFilled, setIsAlreadyFilled] = useState(false);
  const [remainingTime, setRemainingTime] = useState("");
  const [pillar, setPillar] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [numberOfQuestions, setNumberOfQuestions] = useState(0);
  const [submitterName, setSubmitterName] = useState("");
  const [submitterEmployeeId, setSubmitterEmployeeId] = useState("");
  const { width } = useWindowDimensions();
  const isWeb = Platform.OS === "web" && width > 768;

  // Desktop detection
  const { isDesktopWeb } = useDesktopDetect();

  const showAlert = (title, message) => {
    if (Platform.OS === "web") {
      window.alert(`${title}\n\n${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  const getQuarterFromDate = (date) => {
    const month = date.month();
    if (month < 3) return "Q1";
    if (month < 6) return "Q2";
    if (month < 9) return "Q3";
    return "Q4";
  };

  useEffect(() => {
    const checkIfAlreadyFilled = async () => {
      if (loading || !companyId || !topic) return;

      try {
        const now = dayjs();
        const currentQuarter = getQuarterFromDate(now);
        const currentYear = now.year();

        const responsesRef = collection(
          db,
          `companies/${companyId}/questionnaireResponses`
        );

        const q = query(
          responsesRef,
          where("companyId", "==", companyId),
          where("topic", "==", topic)
        );

        const querySnapshot = await getDocs(q);
        let filledThisQuarter = false;

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const responseDate = dayjs(data.timestamp.toDate());
          const responseQuarter = getQuarterFromDate(responseDate);
          const responseYear = responseDate.year();

          if (
            responseQuarter === currentQuarter &&
            responseYear === currentYear
          ) {
            filledThisQuarter = true;
            setSubmitterName(data.userName);
            setSubmitterEmployeeId(data.employeeIdNum);
          }
        });

        if (filledThisQuarter) {
          let nextQuarterStart = now
            .add(3 - (now.month() % 3), "month")
            .startOf("month");
          const diffInHours = nextQuarterStart.diff(now, "hour");
          const months = Math.floor(diffInHours / (24 * 30));
          const days = Math.floor((diffInHours % (24 * 30)) / 24);
          const hours = diffInHours % 24;

          setRemainingTime(
            `${months} month(s), ${days} day(s), and ${hours} hour(s) remaining`
          );
          setIsAlreadyFilled(true);
        }
      } catch (error) {
        console.error("Error checking quarterly status: ", error);
        showAlert("Error", "Failed to check fill status.");
      }
    };

    const fetchQuestions = async () => {
      if (isAlreadyFilled) return;
      if (!isConnected) {
        showAlert("No Internet", "You're currently offline.");
        return;
      }
      if (!user || !user.department) {
        showAlert("Error", "User department not found. Please log in again.");
        return;
      }

      try {
        const departmentRef = doc(db, "Departments", user.department);
        const topicRef = collection(departmentRef, "Topics");
        const querySnapshot = await getDocs(topicRef);

        const topicData = querySnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .find((doc) => doc.topicName === topic);

        if (topicData) {
          setPillar(topicData.Pillar);
          setNumberOfQuestions(topicData.NumberOfQuestions);

          const questionsRef = collection(
            db,
            "Departments",
            user.department,
            "Topics",
            topicData.id,
            "Questions"
          );
          const questionSnapshot = await getDocs(questionsRef);

          let fetchedQuestions = questionSnapshot.docs.map((doc) => {
            const questionData = { id: doc.id, ...doc.data() };

            let optionsWithWeights = questionData.options.map(
              (option, index) => ({
                option,
                weight: questionData.weights[index],
              })
            );

            optionsWithWeights.sort(() => Math.random() - 0.5);

            return {
              ...questionData,
              options: optionsWithWeights.map((item) => item.option),
              weights: optionsWithWeights.map((item) => item.weight),
            };
          });

          if (fetchedQuestions.length === 0) {
            showAlert(
              "No Questions Available",
              "There are no questions for this topic."
            );
          }

          setQuestions(fetchedQuestions);
        } else {
          showAlert(
            "No Questions Available",
            "There are no questions for this topic."
          );
        }
      } catch (error) {
        console.error("Error fetching questions: ", error);
        showAlert(
          "Error",
          "Failed to fetch questions. Please try again later."
        );
      } finally {
        setIsLoading(false);
      }
    };

    checkIfAlreadyFilled();
    fetchQuestions();
  }, [user, loading, companyId, topic, isAlreadyFilled]);

  const handleOptionSelect = (weight) => {
    setResponses((prev) => ({
      ...prev,
      [currentQuestionIndex]: {
        ...(prev[currentQuestionIndex] || {}),
        weight,
      },
    }));
  };

  const handleRemarkChange = (text) => {
    setResponses((prev) => ({
      ...prev,
      [currentQuestionIndex]: {
        ...(prev[currentQuestionIndex] || {}),
        remark: text,
      },
    }));
  };

  const handleNext = () => {
    const currentResponse = responses[currentQuestionIndex];
    if (!currentResponse?.weight) {
      showAlert("Error", "Please select an option before proceeding.");
      return;
    }
    if (!(currentResponse?.remark || "").trim()) {
      showAlert("Error", "Please enter remarks before proceeding.");
      return;
    }

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const currentResponse = responses[currentQuestionIndex];
    if (!currentResponse?.weight) {
      showAlert(
        "Error",
        "Please select an option for the last question before submitting."
      );
      setIsSubmitting(false);
      return;
    }
    if (!(currentResponse?.remark || "").trim()) {
      showAlert("Error", "Please enter remarks before submitting.");
      setIsSubmitting(false);
      return;
    }

    const totalScore = Object.values(responses).reduce((sum, response) => {
      return sum + (response?.weight || 0);
    }, 0);

    const responseDetails = questions.map((question, index) => {
      const response = responses[index] || {};
      const weight = response.weight ?? null;
      const remark = response.remark ?? null;

      return {
        question: question.question,
        selectedOption:
          question.options.find((_, i) => question.weights[i] === weight) || "",
        weight,
        remark,
      };
    });

    try {
      if (!user || !user.employeeId || !user.name) {
        showAlert("Error", "User not authenticated. Please log in again.");
        setIsSubmitting(false);
        return;
      }

      const responsesRef = collection(
        db,
        `companies/${companyId}/questionnaireResponses`
      );
      const q = query(
        responsesRef,
        where("companyId", "==", companyId),
        where("topic", "==", topic)
      );
      const querySnapshot = await getDocs(q);
      const responseCount = querySnapshot.size + 1;

      const responseId = `rs${responseCount}`;

      await addDoc(responsesRef, {
        responseId: responseId,
        topic: topic,
        totalScore: totalScore,
        employeeIdNum: user.employeeIdNum,
        userId: user.employeeId,
        NumberOfQuestions: numberOfQuestions,
        userName: user.name,
        department: user.department,
        companyId: companyId,
        companyName: companyName,
        pillar: pillar,
        timestamp: new Date(),
        quarter: getQuarterFromDate(dayjs()),
        year: dayjs().year(),
        responses: responseDetails,
      });

      showAlert("Success", "Your responses have been submitted!");
      navigation.goBack();
    } catch (error) {
      console.error("Error submitting responses: ", error);
      showAlert(
        "Error",
        "Failed to submit your responses. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || loading) {
    return <Loader />;
  }

  if (isAlreadyFilled) {
    return (
      <FilledMessage
        quarter={getQuarterFromDate(dayjs())}
        remainingTime={remainingTime}
        userName={submitterName}
        employeeIdNum={submitterEmployeeId}
      />
    );
  }

  // Render desktop version
  if (isDesktopWeb) {
    return (
      <DesktopQuestionsView
        topic={topic}
        pillar={pillar}
        questions={questions}
        currentQuestionIndex={currentQuestionIndex}
        responses={responses}
        handleOptionSelect={handleOptionSelect}
        handleRemarkChange={handleRemarkChange}
        handleNext={handleNext}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        navigation={navigation}
        user={user}
      />
    );
  }

  // Mobile version (unchanged)
  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  return (
    <LinearGradient
      colors={GlobalStyles.GlobalBg.gradientColors}
      style={{ flex: 1 }}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
    >
      <View style={styles.container}>
        <Text
          style={[styles.topicText, GlobalStyles.titleTxtOffset]}
        >{`Topic: ${topic}`}</Text>

        {questions.length === 0 ? (
          <Text style={styles.noQuestionsText}>
            No questions available for this topic.
          </Text>
        ) : (
          <>
            {currentQuestion && (
              <QuestionCard
                question={currentQuestion}
                currentIndex={currentQuestionIndex}
                responses={responses}
                handleOptionSelect={handleOptionSelect}
                handleRemarkChange={handleRemarkChange}
                isLastQuestion={isLastQuestion}
                isSubmitting={isSubmitting}
                handleNext={handleNext}
                handleSubmit={handleSubmit}
              />
            )}
            {!isWeb &&
              (!isLastQuestion ? (
                <TouchableOpacity
                  style={[
                    styles.button,
                    GlobalStyles.btnBgShadow,
                    !responses[currentQuestionIndex]?.weight &&
                      styles.buttonDisabled,
                  ]}
                  onPress={handleNext}
                  disabled={!responses[currentQuestionIndex]?.weight}
                >
                  <Text style={[styles.buttonText, GlobalStyles.btntxtoffset]}>
                    Next
                  </Text>
                </TouchableOpacity>
              ) : (
                <View style={styles.submitButtonContainer}>
                  <TouchableOpacity
                    style={[
                      styles.button,
                      GlobalStyles.btnBgShadow,
                      (!responses[currentQuestionIndex]?.weight ||
                        isSubmitting) &&
                        styles.buttonDisabled,
                    ]}
                    onPress={handleSubmit}
                    disabled={
                      !responses[currentQuestionIndex]?.weight || isSubmitting
                    }
                  >
                    <Text
                      style={[styles.buttonText, GlobalStyles.btntxtoffset]}
                    >
                      Submit
                    </Text>
                  </TouchableOpacity>
                  {isSubmitting && (
                    <ActivityIndicator
                      size="small"
                      color="#6200EE"
                      style={{ marginTop: 10 }}
                    />
                  )}
                </View>
              ))}
          </>
        )}
      </View>
    </LinearGradient>
  );
};

// Desktop styles
const desktopStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.contentBg,
  },
  page: {
    flex: 1,
    padding: spacing.lg,
    backgroundColor: colors.contentBg,
    gap: spacing.lg,
  },
  questionArea: {
    flex: 1,
    minHeight: 0,
  },
  header: {
    backgroundColor: colors.cardBg,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButtonIcon: {
    fontSize: 20,
    color: colors.primary,
    marginRight: spacing.sm,
  },
  backButtonText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.primary,
  },

  // Topic Header
  topicHeader: {
    alignItems: 'center',
  },
  pillarBadge: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: borderRadius.full,
    marginBottom: spacing.sm,
  },
  pillarBadgeText: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.small,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  topicTitle: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h2,
    color: colors.textPrimary,
    textAlign: 'center',
    marginBottom: spacing.xs,
  },
  topicSubtitle: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textMuted,
  },

  // Question Card
  questionCard: {
    flex: 1,
    minHeight: 0,
    backgroundColor: colors.cardBg,
    borderRadius: borderRadius.lg,
    ...shadows.md,
    overflow: 'hidden',
  },
  progressHeader: {
    backgroundColor: colors.contentBg,
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  progressInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  questionNumber: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.body,
    color: colors.textSecondary,
  },
  progressBarContainer: {
    flex: 1,
    height: 6,
    backgroundColor: colors.border,
    borderRadius: 3,
    marginLeft: spacing.lg,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: 3,
  },

  // Question Content
  questionLayout: {
    flex: 1,
    minHeight: 0,
    flexDirection: 'row',
    gap: spacing.lg,
    padding: spacing.lg,
  },
  questionColumn: {
    flex: 7,
    minHeight: 0,
  },
  sideColumn: {
    flex: 3,
    minHeight: 0,
    justifyContent: 'space-between',
  },
  questionText: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h4,
    color: colors.textPrimary,
    lineHeight: 28,
    marginBottom: spacing.md,
  },

  // Options
  optionsContainer: {
    gap: spacing.sm,
    paddingBottom: spacing.sm,
  },
  optionsScroll: {
    flex: 1,
    minHeight: 0,
  },
  optionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    borderWidth: 2,
    borderColor: colors.border,
    backgroundColor: colors.cardBg,
  },
  optionButtonSelected: {
    backgroundColor: colors.primaryLight,
  },
  optionButtonHovered: {
    borderColor: colors.textMuted,
  },
  optionRadio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  optionRadioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#fff',
  },
  optionText: {
    flex: 1,
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textPrimary,
  },

  // Remarks
  remarksBlock: {
    flex: 1,
    minHeight: 0,
  },
  remarksLabel: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.body,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
  },
  charCount: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textMuted,
    textAlign: 'right',
    marginTop: spacing.xs,
  },

  // Navigation
  navigationButtons: {
    paddingTop: spacing.md,
  },
  navButton: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderRadius: borderRadius.md,
    ...shadows.sm,
  },
  navButtonDisabled: {
    opacity: 0.5,
  },
  navButtonText: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.body,
    color: '#fff',
    marginRight: spacing.sm,
  },
  navButtonIcon: {
    fontSize: 18,
    color: '#fff',
  },
  submitButton: {
    backgroundColor: colors.success,
  },

  // Empty State
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xxl * 2,
    backgroundColor: colors.cardBg,
    borderRadius: borderRadius.lg,
    ...shadows.sm,
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: spacing.md,
  },
  emptyTitle: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h3,
    color: colors.textPrimary,
    marginBottom: spacing.sm,
  },
  emptyText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textMuted,
    textAlign: 'center',
  },
});

// Mobile styles (unchanged)
const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  topicText: {
    fontSize: GlobalStyles.fontSize.lg,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    marginBottom: 20,
    color: GlobalStyles.btnColor.backgroundColor,
    paddingVertical: 1,
  },
  noQuestionsText: {
    fontSize: GlobalStyles.fontSize.md,
    color: "red",
    textAlign: "center",
    marginTop: 20,
    paddingVertical: 1,
  },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    marginTop: 10,
    width: wp("90%"),
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    borderRadius: 10,
    alignItems: "center",
  },
  buttonDisabled: {
    backgroundColor: "#accdb0",
  },
  buttonText: {
    color: "#fff",
    fontSize: GlobalStyles.fontSize.btn,
    fontWeight: "normal",
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
});

export default QuestionsScreen;
